import { api } from '../../api/client';
import { depsOf } from './moduleCrud';
import type { CrudField } from './moduleCrud';

/** One option in a select's list. */
export interface Option { id: string; name: string }

/**
 * Ordering + caching for cascading `optionsUrl` selects.
 *
 * WHY THIS EXISTS — the naive cascade costs twice what it should. Picking a
 * Parameter on a Permit Limit used to fire FOUR requests in TWO serial waves:
 *
 *   wave 1  Method (by parameter)  +  Units (by parameter, method still EMPTY)
 *   wave 2  Method (identical URL, refetched)  +  Units (now with the method)
 *
 * Wave 2 could not start until wave 1 landed, wave 1's Units answer was thrown
 * away the moment Method auto-selected, and Method was fetched twice with the
 * same URL. Against a remote database that reads as a visible pause and four
 * flashes of the progress bar.
 *
 * The fix is to ask in DEPENDENCY ORDER within a single pass, so a field that
 * follows another cascading field already knows its value before it asks —
 * two requests, no wasted answer, no repeat.
 */

/**
 * Groups cascading fields into dependency LEVELS.
 *
 * Level 0 depends only on ordinary fields; level 1 depends on something in
 * level 0, and so on. Fetching level by level is what lets Units carry the
 * Method that Method's own level just auto-selected.
 *
 * A dependency cycle cannot starve the caller: anything still unplaced once a
 * pass adds nothing is emitted as one final level, so every field is always
 * fetched exactly once.
 */
export function cascadeLevels(dependents: readonly CrudField[]): CrudField[][] {
  const keys = new Set(dependents.map((f) => f.key));
  const levels: CrudField[][] = [];
  const placed = new Set<string>();

  while (placed.size < dependents.length) {
    const level = dependents.filter(
      (f) => !placed.has(f.key)
        // Ready when none of its parents is a cascading field still unplaced.
        && depsOf(f).every((p) => !keys.has(p) || placed.has(p)),
    );
    if (level.length === 0) {
      levels.push(dependents.filter((f) => !placed.has(f.key)));
      break;
    }
    level.forEach((f) => placed.add(f.key));
    levels.push(level);
  }
  return levels;
}

/**
 * Fetches an options list, reusing a previous answer for the same URL.
 *
 * The cache is passed in by the caller. Callers that want reopening a form to
 * be instant pass {@link sharedOptionCache}, whose TTL bounds how stale an
 * entry can get; a caller that genuinely needs a cold read each time can still
 * pass its own Map.
 *
 * `silentLoading` keeps these background lookups off the global progress bar:
 * they are a side effect of a dropdown, not an operation the user started.
 * A failed lookup yields an empty list rather than throwing, so one bad URL
 * cannot abandon the rest of the cascade.
 */
/**
 * Pours a whole bundle of option lists into the cache in one request.
 *
 * The response is keyed by the very URLs {@link fetchOptions} would ask for, so
 * priming needs no knowledge of the cascade — every later lookup simply hits.
 * An entry already in the cache WINS: a list fetched for the live form is at
 * least as fresh as the bundle, and must not be replaced under it.
 *
 * Best-effort throughout. A failure, a malformed body, or a key that no field
 * ever asks for costs nothing but the request; the cascade then fetches exactly
 * as it did before.
 */
/**
 * When each prime URL was last fetched, so a warm one is not re-fetched.
 *
 * This matters more than it looks. The dialog AWAITS the prime for every
 * cascade level past the first, so an unconditional request here means Method
 * and Units wait a full round trip on EVERY open no matter how warm the cache
 * is — the delay survives caching entirely. Skipping the request when the
 * bundle is still fresh is what makes reopening actually instant.
 *
 * Shares {@link SHARED_TTL_MS} with the cache it fills: an entry and the note
 * that it was primed must expire together, or a lapsed cache would never be
 * refilled.
 */
const primedAt = new Map<string, number>();

/**
 * Primes still RUNNING, so concurrent callers share one request.
 *
 * The timestamp above is only written when a prime FINISHES, which left a real
 * gap: the detail page and the dialog it opens both call this, and if the
 * second call starts before the first returns it sees no timestamp and issues
 * its own. Measured on 2026-09-02 - two overlapping cascade-prime requests,
 * 1383 ms and 897 ms, fetching the identical bundle. Sharing the in-flight
 * promise makes the second caller wait on the first instead of duplicating it.
 */
const inFlight = new Map<string, Promise<void>>();

export async function primeOptionCache(
  url: string,
  cache: Map<string, Option[]>,
): Promise<void> {
  const at = primedAt.get(url);
  if (at !== undefined && Date.now() - at <= SHARED_TTL_MS) return;
  const running = inFlight.get(url);
  if (running) return running;

  const run = (async () => {
    try {
      const res = await api.get<{ data?: Record<string, Option[]> }>(
        url, { silentLoading: true } as never);
      for (const [key, opts] of Object.entries(res.data?.data ?? {})) {
        if (Array.isArray(opts) && !cache.has(key)) cache.set(key, opts);
      }
      primedAt.set(url, Date.now());
    } catch {
      /* Optimisation only — the cascade still works unprimed, and NOT recording
         a timestamp means the next open retries rather than trusting a miss. */
    } finally {
      inFlight.delete(url);
    }
  })();
  inFlight.set(url, run);
  return run;
}

/**
 * How long a shared entry stays usable.
 *
 * TEN MINUTES, not one. At 60 s the measured behaviour was: open a permit,
 * read it for a minute, click Edit - and the whole cache had lapsed, so the
 * dialog refetched every list AND re-ran the 900-1400 ms prime (observed
 * 2026-09-02: prime at 15:24:26, full refetch at 15:25:28, 62 s later). That
 * is the ordinary way the screen is used, so the cache was expiring almost
 * every time it mattered.
 *
 * These lists are reference data - analytes, methods, units, limit types,
 * operators - which change on the order of weeks, not minutes. Ten minutes
 * still bounds staleness well inside a working session, and a page reload
 * clears it outright.
 */
const SHARED_TTL_MS = 10 * 60_000;

/**
 * A Map that forgets an entry once it is older than {@link SHARED_TTL_MS}.
 *
 * Callers keep the plain `Map<string, Option[]>` contract — expiry happens
 * inside `get`/`has`, so neither {@link fetchOptions} nor
 * {@link primeOptionCache} needs to know the cache is time-bounded.
 */
class TtlOptionCache extends Map<string, Option[]> {
  private readonly stamps = new Map<string, number>();

  constructor(private readonly ttlMs: number) { super(); }

  override set(key: string, value: Option[]): this {
    this.stamps.set(key, Date.now());
    return super.set(key, value);
  }

  override get(key: string): Option[] | undefined {
    const at = this.stamps.get(key);
    if (at !== undefined && Date.now() - at > this.ttlMs) {
      super.delete(key);
      this.stamps.delete(key);
      return undefined;
    }
    return super.get(key);
  }

  // primeOptionCache guards on `has`, so it must expire too — otherwise a
  // stale-but-present key would block the fresh bundle from replacing it.
  override has(key: string): boolean {
    return this.get(key) !== undefined;
  }
}

/**
 * The cache the detail page and its child dialogs SHARE.
 *
 * WHY SHARED — a per-instance cache is emptied every time a child dialog
 * unmounts, so each Edit click re-paid the full cold cost: the prime, plus a
 * serial round trip per cascade level, against a REMOTE database where the
 * round trip (~276 ms) dwarfs the query (2–115 ms). Reopening the same row
 * refetched lists the page had already loaded moments earlier.
 *
 * Sharing makes the second and later opens memory reads, and lets the parent
 * page's own lookups warm the dialog. The TTL answers the objection the
 * per-form cache existed for: a module-wide cache must not hold a stale list
 * for the whole session, so entries expire after a minute rather than never.
 */
export const sharedOptionCache: Map<string, Option[]> = new TtlOptionCache(SHARED_TTL_MS);

export async function fetchOptions(
  url: string,
  cache: Map<string, Option[]>,
): Promise<Option[]> {
  const hit = cache.get(url);
  if (hit) return hit;
  try {
    // `as never` on the config is the established call shape for this flag -
    // silentLoading lives on the client's own request type, not Axios's.
    const res = await api.get<{ data?: Option[] }>(url, { silentLoading: true } as never);
    const opts = res.data?.data ?? [];
    cache.set(url, opts);
    return opts;
  } catch {
    return [];
  }
}
